import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Grid,
  Divider,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon,
  ClearAll as ClearAllIcon,
} from "@mui/icons-material";
import PreviewPDF from "./PreviewPDF";
import PreviewForm from "./PreviewForm";

const snapshotFields = (currentProcessingFile) => {
  const fields = currentProcessingFile?.document?.tempFields?.formFields ?? [];
  return fields.map((f) => ({ ...f }));
};

const buildPreviewFile = (currentProcessingFile, overrideFields) => ({
  ...currentProcessingFile,
  document: {
    ...currentProcessingFile?.document,
    tempFields: {
      ...currentProcessingFile?.document?.tempFields,
      formFields: overrideFields,
    },
  },
});

const PreviewFormDialog = ({
  open,
  setOpen,
  base64File,
  pdfInput,
  currentProcessingFile,
  setCurrentProcessingFile,
  pageNumber,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
}) => {
  const [previewFields, setPreviewFields] = useState([]);

  useEffect(() => {
    if (open) {
      setPreviewFields(snapshotFields(currentProcessingFile));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const previewProcessingFile = buildPreviewFile(
    currentProcessingFile,
    previewFields,
  );

  const setPreviewProcessingFile = useCallback((updater) => {
    setPreviewFields((prevFields) => {
      const next =
        typeof updater === "function"
          ? updater(buildPreviewFile(currentProcessingFile, prevFields))
          : updater;
      return next?.document?.tempFields?.formFields ?? prevFields;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fillDemoRef = useRef(null);
  const clearRef = useRef(null);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: "90vh", display: "flex", flexDirection: "column" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={600}>Form Preview</Typography>

        {/* Toolbar: Fill Demo + Clear */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Populate all fields with representative sample data">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<AutoFixHighIcon />}
              onClick={() => fillDemoRef.current?.()}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Fill with Demo Data
            </Button>
          </Tooltip>

          <Tooltip title="Reset all fields to empty">
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={() => clearRef.current?.()}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Clear
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          overflow: "auto",
          backgroundColor: "#f5f5f5",
          px: 2,
          py: 0,
        }}
      >
        <Grid container sx={{ overflow: "hidden", height: "100%" }}>
          <Grid item xs={12} sm={6} sx={{ overflowY: "auto", height: "100%" }}>
            <PreviewPDF
              base64File={base64File}
              pdfInput={pdfInput}
              currentProcessingFile={previewProcessingFile}
              setCurrentProcessingFile={setPreviewProcessingFile}
              pageNumber={pageNumber}
              onDocumentLoadSuccess={onDocumentLoadSuccess}
              onPageLoadSuccess={onPageLoadSuccess}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ overflowY: "auto", height: "100%" }}>
            <PreviewForm
              currentProcessingFile={previewProcessingFile}
              setCurrentProcessingFile={setPreviewProcessingFile}
              fillDemoRef={fillDemoRef}
              clearRef={clearRef}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => setOpen(false)}
          variant="contained"
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewFormDialog;
